import { Component, effect, inject, signal, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { Auth } from '../../../services/auth';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { environment } from '../../../../environments/environment';
import { ForgotPassword } from '../forgot-password/forgot-password';
import { finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TwoFactorMethod } from '../../../types/twofa';

interface DemoUser {
  label: string;
  username: string;
  password: string;
}

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, MatButtonModule, MatInputModule, MatIconModule, MatProgressSpinnerModule, MatSelectModule, NgOptimizedImage],
  templateUrl: './login.html',
  styleUrl: './login.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Login implements OnInit {

  imgSrc = environment.logoUrl;
  isDemo = environment.demo;

  private authService = inject(Auth);
  private dialog = inject(MatDialog);
  private toastr = inject(ToastrService);
  private route = inject(ActivatedRoute);

  isLoading = signal(false);
  hidePassword = signal(true);

  // --- Staged-login 2FA step ---
  twoFactorRequired = signal(false);
  isVerifying = signal(false);
  isSendingCode = signal(false);
  private preAuthToken = signal<string | null>(null);
  availableMethods = signal<string[]>([]);
  methodControl = new FormControl<TwoFactorMethod>('TOTP', { nonNullable: true });
  twoFactorCode = new FormControl('', { nonNullable: true, validators: [Validators.required] });

  loginForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  demoUsers: DemoUser[] = [
    { label: 'Admin', username: 'Admin', password: 'Admin' },
    { label: 'Bekuldo', username: 'Bekuldo', password: 'Bekuldo' },
    { label: 'Leletezo', username: 'Leletezo', password: 'Leletezo' },
  ];

  selectedDemoUser = signal<DemoUser | null>(null);

  constructor() {
    // An external-token auto-login (handled in app.ts) can complete with a
    // staged 2FA challenge instead of a token; pick it up and show the code step.
    effect(() => {
      const pending = this.authService.pendingTwoFactor();
      if (pending?.twoFactorRequired) {
        this.authService.pendingTwoFactor.set(null);
        this.enterTwoFactorStep(pending.preAuthToken ?? null, pending.methods ?? [], pending.preferredMethod);
      }
    });
  }

  ngOnInit(): void {
    // When an external token is in the URL, app.ts handles auth + navigation.
    // Skipping here prevents a race where this component navigates with the
    // stale JWT in localStorage before the external-token login replaces it.
    if (this.route.snapshot.queryParamMap.get('token')) {
      return;
    }
    this.authService.initializeAuth(true);
  }

  onDemoUserSelected(user: DemoUser) {
    this.selectedDemoUser.set(user);
    this.loginForm.setValue({ username: user.username, password: user.password });
  }

  login() {
    if (this.isLoading()) return;
    if (this.loginForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading.set(true);

    const { username, password } = this.loginForm.value;

    this.authService.login(username!, password!)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.twoFactorRequired) {
            this.enterTwoFactorStep(response.preAuthToken ?? null, response.methods ?? [], response.preferredMethod);
          }
          // Otherwise navigation is handled by the auth service after user load.
        },
        error: () => {
          this.toastr.error('Hiba történt a bejelentkezés során!', 'Hiba');
        }
      });
  }

  private enterTwoFactorStep(preAuthToken: string | null, methods: string[], preferred?: string): void {
    this.preAuthToken.set(preAuthToken);
    this.availableMethods.set(methods);
    const initial = (preferred && preferred !== 'NONE' ? preferred : methods[0]) as TwoFactorMethod;
    this.methodControl.setValue(initial ?? 'TOTP');
    this.twoFactorCode.reset();
    this.twoFactorRequired.set(true);
  }

  verifyTwoFactor(): void {
    const code = this.twoFactorCode.value.trim();
    const token = this.preAuthToken();
    if (!code || !token || this.isVerifying()) return;

    this.isVerifying.set(true);
    this.authService.verifyLoginTwoFactor(token, this.methodControl.value, code)
      .pipe(finalize(() => this.isVerifying.set(false)))
      .subscribe({
        next: () => {
          // Auth service stores the token and navigates by role.
        },
        error: (err) => {
          if (err?.status === 401) {
            this.toastr.error('A munkamenet lejárt, jelentkezzen be újra.', 'Hiba');
            this.cancelTwoFactor();
          } else {
            this.toastr.warning('Érvénytelen kód.', 'Sikertelen');
          }
        }
      });
  }

  sendEmailCode(): void {
    const token = this.preAuthToken();
    if (!token || this.isSendingCode()) return;
    this.isSendingCode.set(true);
    this.authService.sendLoginEmailCode(token)
      .pipe(finalize(() => this.isSendingCode.set(false)))
      .subscribe({
        next: () => this.toastr.info('Belépési kód elküldve (fejlesztői módban a szerver naplójában látható).', 'Kód elküldve'),
        error: () => this.toastr.error('Nem sikerült elküldeni a kódot.', 'Hiba'),
      });
  }

  cancelTwoFactor(): void {
    this.twoFactorRequired.set(false);
    this.preAuthToken.set(null);
    this.availableMethods.set([]);
    this.twoFactorCode.reset();
    this.loginForm.controls.password.reset();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }

  forgotPassword() {
    this.dialog.open(ForgotPassword, {
      minWidth: '800px',
      disableClose: false,
    });
  }

}
